import { CreditCard, MapPin, Lock } from "lucide-react";
import { StudentIcon } from "../lib/icons";
import { Input } from "./ui/input";
import { SchoolInput } from "./SchoolInput";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC","PR",
];

// Formatting helpers exported for the parent (to keep raw digits + display value aligned).
export const formatCardInput = (raw) => raw.replace(/\D/g, "").slice(0, 19).replace(/(\d{4})(?=\d)/g, "$1 ");
export const formatExpiryInput = (raw) => {
  const digits = raw.replace(/\D/g, "").slice(0, 4);
  return digits.length <= 2 ? digits : `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const Section = ({ icon: Icon, title, children }) => (
  <div className="rounded-md border border-border bg-bone p-4">
    <div className="flex items-center gap-2 text-[13px] font-semibold text-ink">
      <Icon className="h-4 w-4 text-tangerine" strokeWidth={2} />
      {title}
    </div>
    <div className="mt-3 space-y-2.5">{children}</div>
  </div>
);

const Err = ({ msg }) => msg ? <p className="mt-1 text-[13px] text-scarlet">{msg}</p> : null;

// Controlled donor + payment fields. Parent manages state, validation, and submission.
export const DonorFieldsSection = ({ donor, payment, errors, onDonorChange, onPaymentChange }) => {
  const set = (obj, key) => (val) => onDonorChange({ ...obj, [key]: val });
  const setP = (obj, key) => (val) => onPaymentChange({ ...obj, [key]: val });

  return (
    <div className="space-y-3" data-testid="donor-fields">
      <Section icon={MapPin} title="Contact + billing address">
        <div>
          <Input
            data-testid="donor-fullName"
            value={donor.fullName}
            onChange={(e) => onDonorChange({ ...donor, fullName: e.target.value })}
            placeholder="Full name (as it should appear on the receipt)"
          />
          <Err msg={errors.fullName} />
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <div>
            <Input
              data-testid="donor-email"
              type="email"
              value={donor.email}
              onChange={(e) => onDonorChange({ ...donor, email: e.target.value })}
              placeholder="Email"
            />
            <Err msg={errors.email} />
          </div>
          <div>
            <Input
              data-testid="donor-phone"
              type="tel"
              value={donor.phone}
              onChange={(e) => onDonorChange({ ...donor, phone: e.target.value })}
              placeholder="Phone"
            />
            <Err msg={errors.phone} />
          </div>
        </div>
        <div>
          <Input
            data-testid="donor-street"
            value={donor.street}
            onChange={(e) => onDonorChange({ ...donor, street: e.target.value })}
            placeholder="Street address"
          />
          <Err msg={errors.street} />
        </div>
        <div className="grid grid-cols-[1fr_5rem_6rem] gap-2.5">
          <div>
            <Input
              data-testid="donor-city"
              value={donor.city}
              onChange={(e) => onDonorChange({ ...donor, city: e.target.value })}
              placeholder="City"
            />
            <Err msg={errors.city} />
          </div>
          <div>
            <select
              data-testid="donor-state"
              value={donor.state}
              onChange={(e) => onDonorChange({ ...donor, state: e.target.value })}
              className="flex h-10 w-full rounded-md border border-input bg-background px-2 text-base text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">State</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <Err msg={errors.state} />
          </div>
          <div>
            <Input
              data-testid="donor-zip"
              value={donor.zip}
              onChange={(e) => onDonorChange({ ...donor, zip: e.target.value })}
              placeholder="ZIP"
              inputMode="numeric"
            />
            <Err msg={errors.zip} />
          </div>
        </div>
      </Section>

      <Section icon={StudentIcon} title="Your schools (optional)">
        <SchoolInput
          testid="donor-undergrad"
          value={donor.undergrad}
          onChange={set(donor, "undergrad")}
          placeholder="Undergraduate school"
        />
        <SchoolInput
          testid="donor-grad"
          value={donor.grad}
          onChange={set(donor, "grad")}
          placeholder="Graduate school"
        />
      </Section>

      <Section icon={CreditCard} title="Payment">
        <div>
          <Input
            data-testid="donor-card"
            value={payment.card}
            onChange={(e) => onPaymentChange({ ...payment, card: formatCardInput(e.target.value) })}
            placeholder="Card number"
            inputMode="numeric"
            autoComplete="cc-number"
          />
          <Err msg={errors.card} />
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <div>
            <Input
              data-testid="donor-expiry"
              value={payment.expiry}
              onChange={(e) => onPaymentChange({ ...payment, expiry: formatExpiryInput(e.target.value) })}
              placeholder="MM/YY"
              inputMode="numeric"
              autoComplete="cc-exp"
            />
            <Err msg={errors.expiry} />
          </div>
          <div>
            <Input
              data-testid="donor-cvc"
              value={payment.cvc}
              onChange={(e) => onPaymentChange({ ...payment, cvc: e.target.value.replace(/\D/g, "").slice(0, 4) })}
              placeholder="CVC"
              inputMode="numeric"
              autoComplete="cc-csc"
            />
            <Err msg={errors.cvc} />
          </div>
        </div>
        <p className="flex items-center gap-1.5 text-[13px] text-olive">
          <Lock className="h-3 w-3" strokeWidth={2} />
          Prototype only. No real charge. Secured by Stripe at launch.
        </p>
      </Section>
    </div>
  );
};
